import { useState, type FormEvent } from "react";
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label } from "@wa/ui";
import { Mail } from "lucide-react";
import { useT, useI18n } from "../../../i18n/I18nProvider";
import { api, ApiError } from "../../../lib/api";
import { requestSetPassword, type AccountUser } from "../../../lib/auth";

// ── Password ────────────────────────────────────────────────────────────
export function PasswordCard({ user, onChanged }: { user: AccountUser; onChanged: () => void }) {
  const t = useT();
  const { locale } = useI18n();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // "Set password" (OAuth-only user) - a setup link by email.
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkSentTo, setLinkSentTo] = useState<string | null>(null);
  const [linkErr, setLinkErr] = useState(false);

  async function sendSetupLink() {
    setLinkBusy(true);
    setLinkErr(false);
    try {
      const res = await requestSetPassword(locale);
      setLinkSentTo(res.sentTo);
    } catch {
      setLinkErr(true);
    } finally {
      setLinkBusy(false);
    }
  }

  // A user without a password (Google sign-in) cannot change the password -
  // offer the email-confirmed "set password" flow instead.
  if (!user.hasPassword) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("security.password")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 max-w-md">
          <p className="text-sm text-muted-foreground">{t("security.noPasswordInfo")}</p>
          {linkSentTo && <p className="text-sm text-emerald-600">{t("security.setupLinkSent", { email: linkSentTo })}</p>}
          {linkErr && <p className="text-sm text-destructive">{t("common.error")}</p>}
          <Button onClick={sendSetupLink} loading={linkBusy} disabled={!!linkSentTo}>
            <Mail className="mr-1 size-4" />
            {t("security.sendSetupLink")}
          </Button>
        </CardContent>
      </Card>
    );
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setMsg(null);
    if (next.length < 8) {
      setMsg({ ok: false, text: t("security.passwordTooShort") });
      return;
    }
    setBusy(true);
    try {
      await api.post("/me/password", { currentPassword: current, newPassword: next });
      setMsg({ ok: true, text: t("security.passwordChanged") });
      setCurrent("");
      setNext("");
      onChanged();
    } catch (err) {
      const wrong = err instanceof ApiError && err.body && typeof err.body === "object" && (err.body as { error?: string }).error === "wrong_password";
      setMsg({ ok: false, text: wrong ? t("security.wrongPassword") : t("common.error") });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("security.password")}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-4 max-w-md">
          <div className="space-y-1.5">
            <Label htmlFor="cur">{t("security.currentPassword")}</Label>
            <Input id="cur" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new">{t("security.newPassword")}</Label>
            <Input id="new" type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} required />
          </div>
          {msg && <p className={msg.ok ? "text-sm text-emerald-600" : "text-sm text-destructive"}>{msg.text}</p>}
          <Button type="submit" loading={busy} disabled={!current || !next}>
            {t("security.changePassword")}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
